import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { Fragment, useEffect, useState } from 'react';
import { dateFormat } from '@/utils';
import { Link, useNavigate } from 'react-router-dom';
import { useApprovalTasks } from '@/hooks/api/1980_approval_tasks';
import { useApprovalSearchCondition } from '@/providers/ApprovalSearchConditionProvider';
import { PaymentTask, PurchaseOrderContractTask, QuotationTask, SalesOrderContractTask } from '@/hooks/api/models';
import { PurchaseOrderLogic, SalesOrderLogic } from '@/hooks/api/models/logics';
import { MotionDiv } from '@/components/element/MotionDiv';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';

interface ApprovalContent {
  link: string;
  category: string;
  type: string;
  task_title: string;
  project: string;
  phase_id: number;
  phase: string;
  amount: string;
  icon_url: string;
  user_name: string;
  bp: string;
  team: string;
  due_date?: string;
}

export const ListElement = () => {
  const { setShowLoading } = useLoading();
  const navigate = useNavigate();
  const { _w_ } = useCurrentWorkspace();
  const { approvalSearchCondition: searchCondition } = useApprovalSearchCondition();
  const [approvalContents, setApprovalContents] = useState<ApprovalContent[]>([]);
  const {
    data: tasks,
    isPending,
    refetch,
  } = useApprovalTasks({
    ...searchCondition,
    identifier: _w_!.identifier,
  });

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (tasks) {
      setApprovalContents(
        tasks.map((t) => {
          if (t.task_name == 'sales_order_contract_task') {
            const d = t.sales_order_contract_task!;
            return {
              link: `/w/${_w_!.identifier}/approval/teams/${d.sales_order.team_outline.id}/sales_orders/${
                d.sales_order.id
              }/sales_order_contract_tasks/${d.id}`,
              category: '売上',
              type: '契約',
              task_title: d.name,
              project: d.sales_order.name,
              phase_id: d.sales_order_contract_task_phase.id,
              phase: d.sales_order_contract_task_phase.label,
              amount: `¥${SalesOrderLogic.totalAmount(d.sales_order).toLocaleString()}`,
              icon_url: d.sales_order.user.user_account.icon_file_url,
              user_name: d.sales_order.user.user_account.name,
              bp: d.sales_order.business_partner.display_name,
              team: d.sales_order.team_outline.name,
              due_date: d.due_date,
            };
          } else if (t.task_name == 'purchase_order_contract_task') {
            const d = t.purchase_order_contract_task!;
            return {
              link: `/w/${_w_!.identifier}/approval/teams/${d.purchase_order.team_outline.id}/purchase_orders/${
                d.purchase_order.id
              }/purchase_order_contract_tasks/${d.id}`,
              category: '費用',
              type: '契約',
              task_title: d.name,
              project: d.purchase_order.name,
              phase_id: d.purchase_order_contract_task_phase.id,
              phase: d.purchase_order_contract_task_phase.label,
              amount: `¥${PurchaseOrderLogic.totalAmount(d.purchase_order).toLocaleString()}`,
              icon_url: d.purchase_order.user.user_account.icon_file_url,
              user_name: d.purchase_order.user.user_account.name,
              bp: d.purchase_order.business_partner.display_name,
              team: d.purchase_order.team_outline.name,
              due_date: d.due_date,
            };
          } else {
            const d = t.quotation_task!;
            return {
              link: `/w/${_w_!.identifier}/approval/teams/${d.sales_order.team_outline.id}/sales_orders/${
                d.sales_order.id
              }/quotation_tasks/${d.id}`,
              category: '売上',
              type: '見積',
              task_title: d.name,
              project: d.sales_order.name,
              phase_id: d.quotation_task_phase.id,
              phase: d.quotation_task_phase.label,
              amount: `¥${SalesOrderLogic.totalAmount(d.sales_order).toLocaleString()}`,
              icon_url: d.sales_order.user.user_account.icon_file_url,
              user_name: d.sales_order.user.user_account.name,
              bp: d.sales_order.business_partner.display_name,
              team: d.sales_order.team_outline.name,
              due_date: d.due_date,
            };
          }
        })
      );
    }
  }, [tasks]);

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const assignTemplate = (a: ApprovalContent) => (
    <div className="d-flex align-items-center">
      <img src={a.icon_url} className="avatar avatar-xs me-2 rounded-circle" alt="avatar image" />
      <h6 className="mb-0 text-xxs">{a.user_name}</h6>
    </div>
  );

  const dueDateTemplate = (a: ApprovalContent) => (a.due_date ? dateFormat({ date: a.due_date }) : '-');

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(`approval_${_w_!.id}`);

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  if (tasks == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">承認待ちタスク一覧</h5>
        </div>
        <div>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              className="form-control"
              placeholder="検索"
              type="text"
              value={filters.global.value}
              onChange={(e) => {
                const value = e.target.value;
                let _filters = { ...filters };
                _filters.global.value = value;
                setFilters(_filters);
                onKeywordChanged(value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="card-body">
        {tasks.length == 0 && <p className="card-description text-sm">登録されている承認タスクがありません。</p>}

        {approvalContents.length > 0 && (
          <div className="table-responsive">
            <DataTable
              value={approvalContents}
              stripedRows
              scrollable
              scrollHeight="800px"
              tableClassName="table data-table align-items-center clickable-row"
              removableSort
              onRowClick={(e) => {
                navigate(e.data.link);
              }}
              filters={filters}
              globalFilterFields={['category', 'type', 'bp', 'task_title', 'project', 'phase', 'user_name', 'team']}
              emptyMessage="No Data."
              onSort={onSort}
              sortField={dataTableSortContent.dataTableSortField?.filed}
              sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
            >
              <Column
                header="プロジェクト種別"
                field="category"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  width: '50px',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="申請タスク種別"
                field="type"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  width: '50px',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="取引先"
                field="bp"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '100px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="タスク名"
                field="task_title"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '200px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="プロジェクト名"
                field="project"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '150px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                sortable
              ></Column>
              <Column
                header="フェーズ"
                field="phase"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ minWidth: '50px' }}
                sortable
              ></Column>
              <Column
                header="金額"
                field="amount"
                bodyClassName="text-xxs px-2 font-weight-bold text-end pe-4"
                headerClassName="text-uppercase text-xxs font-weight-bolder text-end"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="プロジェクト担当者"
                field="user_name"
                body={assignTemplate}
                bodyClassName="text-xxs px-2"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="チーム名"
                field="purchase_order.team_outline.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="期限日"
                field="due_date"
                body={dueDateTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
            </DataTable>
          </div>
        )}
      </div>
    </MotionDiv>
  );
};
